import React, { useState } from "react";
import { toast } from "sonner";
import { Linkedin, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function LinkedInImportDialog({ onImported }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [importing, setImporting] = useState(false);

  const doImport = async () => {
    if (text.trim().length < 30) {
      toast.error("Paste more of your LinkedIn profile (About, Experience, Education sections)");
      return;
    }
    setImporting(true);
    try {
      const { data } = await api.post("/resume-builder/import-linkedin", { linkedin_text: text });
      onImported(data);
      setOpen(false);
      setText("");
      toast.success("Imported from LinkedIn — review and edit below");
    } catch (e) {
      toast.error("Could not parse that — try pasting more complete profile text");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="resume-linkedin-import-open-button" className="flex items-center gap-1.5 text-xs font-semibold text-sky-300 hover:text-sky-200">
          <Linkedin className="w-3.5 h-3.5" /> Import from LinkedIn
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="resume-linkedin-import-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Import from LinkedIn</DialogTitle>
          <DialogDescription>Copy your LinkedIn profile's About, Experience, and Education sections and paste below — AI will structure it into your resume.</DialogDescription>
        </DialogHeader>
        <textarea
          value={text} onChange={(e) => setText(e.target.value)} rows={8} data-testid="resume-linkedin-import-textarea"
          placeholder="Paste your LinkedIn profile text here (About, Experience, Education)..."
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-sky-400 resize-none"
        />
        <button onClick={doImport} disabled={importing} data-testid="resume-linkedin-import-submit-button" className="flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors">
          {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Linkedin className="w-4 h-4" />} Import & Structure
        </button>
      </DialogContent>
    </Dialog>
  );
}
